import { useMemo } from 'react'
import { Navigate, useParams } from 'react-router-dom'
import { AnalyticsDashboardLayout, PeriodSelector } from '@/components/analytics'
import {
  ChurnRiskTable,
  ClientLifecycleKpiStrip,
  VisitFrequencyChart,
} from '@/components/clients'
import { Card } from '@/components/ui'
import { LoadingScreen } from '@/components/LoadingScreen'
import { useAnalyticsTimeFilter } from '@/hooks/useAnalyticsTimeFilter'
import { useChurnRisk, useVisitFrequency } from '@/hooks/useStudioClientLifecycle'
import { useStudioPeriods } from '@/hooks/useStudioPeriods'

// Решения владельца 02.09.2026: спящий — 14..45 дней без визита (дальше это
// уже не возврат, а другая работа), «ценным» человека делает абонемент
// Базовый/Безлимитный (порог по числу визитов снят, он в RPC не участвует).
// ⚠️ Воронки «Пробный → Базовый» здесь нет намеренно: admissions_raw — лог
// ПОСЕЩЕНИЙ, а не покупок, поэтому и доля, и «среднее время до базового»
// датировались первым приходом по абонементу, а не решением клиента (купить
// могли в день пробного, а прийти через четыре дня). Метрику вернём, когда
// появится источник продаж, а не расписания.
const CHURN_IDLE_DAYS_MIN = 14
const CHURN_IDLE_DAYS_MAX = 45
const TREND_FLOOR_DATE = '2026-02-01'

export function StudioClientLifecyclePage() {
  const { partnerNumber, studioId } = useParams<{ partnerNumber?: string; studioId?: string }>()
  const studioInternalId = Number(studioId || 0)
  const periods = useStudioPeriods(studioInternalId || undefined)
  const currentPeriod = periods.currentPeriod
  const { selectedTimeFilter, setSelectedTimeFilter, timeFilterOptions, selectedRange } = useAnalyticsTimeFilter(currentPeriod)
  const trendRange = useMemo(() => {
    if (!currentPeriod || periods.availablePeriods.length === 0) return null
    const currentIndex = periods.availablePeriods.findIndex((period) => period.period_id === currentPeriod.period_id)
    if (currentIndex < 0) return null

    const earliestAllowedIndex = periods.availablePeriods.findIndex((period) => period.period_start > TREND_FLOOR_DATE)
    const floorIndex = earliestAllowedIndex >= 0 ? earliestAllowedIndex : 0
    const fromIndex = Math.max(floorIndex, currentIndex - 5)
    const fromPeriod = periods.availablePeriods[fromIndex]

    return {
      dateFrom: fromPeriod.period_start,
      dateTo: currentPeriod.period_end,
    }
  }, [currentPeriod, periods.availablePeriods])

  const churnRisk = useChurnRisk({
    studioInternalId: studioInternalId || null,
    idleDaysMin: CHURN_IDLE_DAYS_MIN,
    idleDaysMax: CHURN_IDLE_DAYS_MAX,
  })
  const visitFrequency = useVisitFrequency({
    studioInternalId: studioInternalId || null,
    dateFrom: trendRange?.dateFrom ?? null,
    dateTo: trendRange?.dateTo ?? null,
  })
  const selectedFrequency = useVisitFrequency({
    studioInternalId: studioInternalId || null,
    dateFrom: selectedRange?.start ?? null,
    dateTo: selectedRange?.end ?? null,
  })

  const kpi = useMemo(() => {
    const latestFrequency = selectedFrequency.data[selectedFrequency.data.length - 1]

    return {
      // «Посетило» — любой визит, «ходят регулярно» — не меньше одного
      // визита в среднем за неделю выбранного диапазона.
      visitedClients: latestFrequency?.activeClients ?? 0,
      regularClients: latestFrequency?.regularClients ?? null,
      periodInProgress: latestFrequency?.isCurrentPeriod ?? false,
      daysElapsed: latestFrequency?.daysElapsed ?? 0,
      daysTotal: latestFrequency?.daysTotal ?? 0,
      churnRiskCount: churnRisk.data.length,
    }
  }, [selectedFrequency.data, churnRisk.data.length])

  if (!partnerNumber || !studioId || !Number.isFinite(studioInternalId) || studioInternalId <= 0) {
    return <Navigate to="/partners" replace />
  }

  if (periods.loading) {
    return <LoadingScreen />
  }

  if (periods.error) {
    return (
      <div className="min-h-screen bg-background p-4">
        <Card className="p-6 text-center text-error">{periods.error}</Card>
      </div>
    )
  }

  return (
    <AnalyticsDashboardLayout containerClassName="max-w-none mx-0 space-y-3" showBottomPadding>
      <PeriodSelector
        currentPeriod={currentPeriod}
        onPrevPeriod={periods.goToPrevPeriod}
        onNextPeriod={periods.goToNextPeriod}
        canGoPrev={periods.canGoPrev}
        canGoNext={periods.canGoNext}
        showViewModeSelector
        isCurrentPeriod={periods.isCurrentPeriod}
        periodFilterValue={selectedTimeFilter}
        periodFilterOptions={timeFilterOptions}
        onPeriodFilterChange={setSelectedTimeFilter}
        selectedRange={selectedRange}
      />

      <ClientLifecycleKpiStrip
        visitedClients={kpi.visitedClients}
        regularClients={kpi.regularClients}
        periodInProgress={kpi.periodInProgress}
        daysElapsed={kpi.daysElapsed}
        daysTotal={kpi.daysTotal}
        churnRiskCount={kpi.churnRiskCount}
        idleDaysMin={CHURN_IDLE_DAYS_MIN}
        idleDaysMax={CHURN_IDLE_DAYS_MAX}
      />

      <ChurnRiskTable
        rows={churnRisk.data}
        loading={churnRisk.loading}
        error={churnRisk.error}
        idleDaysMin={CHURN_IDLE_DAYS_MIN}
        idleDaysMax={CHURN_IDLE_DAYS_MAX}
      />

      <VisitFrequencyChart rows={visitFrequency.data} loading={visitFrequency.loading} error={visitFrequency.error} />
    </AnalyticsDashboardLayout>
  )
}
